import React, { useMemo, useState } from 'react';
import { ListIcon, SlidersHorizontalIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { CategoryKey } from './categoryData';
type CatalogView = 'grid' | 'list';
type SortOption = 'default' | 'name-asc' | 'name-desc';
type HomeDecorCatalogProps = {
  onCategorySelect: (categoryKey: CategoryKey) => void;
};
type HomeProduct = {
  name: string;
  collection: string;
  image: string;
};
const EXPLORER_CATEGORIES: {
  key: CategoryKey;
  label: string;
  count: number;
}[] = [{
  key: 'alimentation',
  label: 'Alimentation',
  count: 47
}, {
  key: 'boisson',
  label: 'Boisson',
  count: 25
}, {
  key: 'electronique',
  label: 'Électronique',
  count: 6
}, {
  key: 'maison-decoration',
  label: 'Maison et Décoration',
  count: 16
}];
const PRODUCTS: HomeProduct[] = [{
  name: 'Autocuiseur inox 6 L – cuisson rapide',
  collection: 'Cuisine · Cuisson',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/45c971b3-140c-4859-8a48-4c0ce4d0494f.jpg'
}, {
  name: 'Batterie de cuisine 10 pièces inox',
  collection: 'Cuisine · Ustensiles',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/4eefa755-8266-4441-862e-86d68d6cbb93.jpg'
}, {
  name: 'Blender haute performance multifonction',
  collection: 'Petit électroménager',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/57cd8613-7675-47a3-87aa-a82a8c305fc1.jpg'
}, {
  name: 'Service de table en céramique blanche',
  collection: 'Arts de la table',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/870eabcd-67fa-4c09-87b2-f47441803229.jpg'
}, {
  name: 'Autocuiseur familial en acier inoxydable',
  collection: 'Cuisine · Cuisson',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/45c971b3-140c-4859-8a48-4c0ce4d0494f.jpg'
}, {
  name: 'Set de casseroles et poêles empilables',
  collection: 'Cuisine · Ustensiles',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/4eefa755-8266-4441-862e-86d68d6cbb93.jpg'
}];
export function HomeDecorCatalog({
  onCategorySelect
}: HomeDecorCatalogProps) {
  const [sort, setSort] = useState<SortOption>('default');
  const [view, setView] = useState<CatalogView>('grid');
  const products = useMemo(() => {
    if (sort === 'default') return PRODUCTS;
    return [...PRODUCTS].sort((first, second) => {
      const result = first.name.localeCompare(second.name, 'fr');
      return sort === 'name-asc' ? result : -result;
    });
  }, [sort]);
  return <section className="mt-8 overflow-hidden rounded-2xl bg-white shadow-[0_18px_45px_rgba(10,51,32,0.12)]" aria-labelledby="home-decor-catalog-title" data-id="element-280" data-mp="141tpmcpenby9">
      <div className="relative overflow-hidden bg-[#f4fae8] px-6 py-12 sm:px-10 lg:px-14 lg:py-16" data-id="element-281" data-mp="1903n4tylqon1">
        <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full border-[26px] border-ardis-yellow/35" aria-hidden="true" data-id="element-282" data-mp="1dtfxq06g4uws" />
        <div className="absolute bottom-0 right-[15%] h-24 w-24 rounded-full border-[18px] border-ardis-green/10" aria-hidden="true" data-id="element-283" data-mp="lbkda9zwm3su" />
        <div className="relative" data-id="element-284" data-mp="dr9sgb6gzglj">
          <p className="text-sm text-gray-500" data-id="element-285" data-mp="dq8enyy2kg04">Accueil <span aria-hidden="true" data-id="element-286" data-mp="8sq11v93xcls">›</span> <span className="font-medium text-ardis-green" data-id="element-287" data-mp="8trgsd12in9u">Maison et Décoration</span></p>
          <h3 id="home-decor-catalog-title" className="mt-4 font-display text-4xl font-bold tracking-tight text-ardis-green sm:text-5xl" data-id="element-288" data-mp="3stzs3rsnz9x">Maison et Décoration</h3>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base" data-id="element-289" data-mp="3qykz0j1ad88s">Découvrez une sélection d’équipements de cuisine, d’articles de table et d’objets pratiques disponibles sur devis.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[230px_minmax(0,1fr)]" data-id="element-290" data-mp="3ha2z040tz567">
        <aside className="border-b border-gray-100 bg-gray-50/70 p-6 lg:border-b-0 lg:border-r lg:p-8" aria-label="Explorer les catégories" data-id="element-291" data-mp="3ca8csds033rh">
          <h4 className="font-display text-xl font-bold text-ardis-green" data-id="element-292" data-mp="3871yciuqi69x">Explorer</h4>
          <ul className="mt-5 space-y-1 border-t border-gray-200 pt-4" data-id="element-293" data-mp="33abinwmudcp1">
            {EXPLORER_CATEGORIES.map(category => {
            const isCurrent = category.key === 'maison-decoration';
            return <li key={category.key} data-id="element-294" data-mp="1b6ny9eut41ma">
                  <button type="button" onClick={() => onCategorySelect(category.key)} aria-current={isCurrent ? 'page' : undefined} className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ardis-blue focus:ring-offset-2 ${isCurrent ? 'text-ardis-green' : 'text-gray-700 hover:bg-white hover:text-ardis-green'}`} data-id="element-295" data-mp="1b9tk8l852nds">
                    <span className="flex items-center gap-2" data-id="element-296" data-mp="ehq15pzl3rly"><span className="text-ardis-yellow" aria-hidden="true" data-id="element-297" data-mp="eirootxse6kk">›</span>{category.label}</span>
                    <span className={isCurrent ? 'text-ardis-yellow' : 'text-gray-400'} data-id="element-298" data-mp="jh2zp7ztin4d">({category.count})</span>
                  </button>
                </li>;
          })}
          </ul>
        </aside>

        <div className="min-w-0 p-6 sm:p-8" data-id="element-299" data-mp="2du28oi9p0j2o">
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-300" data-mp="2nyiqvianac9n">
            <p className="text-sm font-medium text-gray-700" data-id="element-301" data-mp="2j07f320shros">Affichage de <span className="font-semibold text-ardis-green" data-id="element-302" data-mp="2ixl7j2dwfc6k">1–{products.length}</span> sur 16 résultats</p>
            <div className="flex flex-wrap items-center gap-2" aria-label="Options d’affichage et de tri" data-id="element-303" data-mp="2s0497j5y7ir8">
              <div className="flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Mode d’affichage" data-id="element-304" data-mp="36oqo5jrny8hx">
                <button type="button" aria-label="Afficher en grille" aria-pressed={view === 'grid'} onClick={() => setView('grid')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'grid' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-305" data-mp="31oujrjf20f1u"><div className="h-4 w-4" aria-hidden="true" data-id="element-306" data-mp="1lavzaasls3c9" /></button>
                <button type="button" aria-label="Afficher en liste" aria-pressed={view === 'list'} onClick={() => setView('list')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'list' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-307" data-mp="1ha2c5jmziskf"><ListIcon className="h-4 w-4" aria-hidden="true" data-id="element-308" data-mp="3lfqemxf1p3xp" /></button>
              </div>
              <label className="relative flex items-center" data-id="element-309" data-mp="3qf313h2088zr">
                <SlidersHorizontalIcon className="pointer-events-none absolute left-3 h-4 w-4 text-ardis-green" aria-hidden="true" data-id="element-310" data-mp="3vcuqo4f6fj0s" />
                <span className="sr-only" data-id="element-311" data-mp="367obdt5qrxk">Trier les produits</span>
                <select value={sort} onChange={event => setSort(event.target.value as SortOption)} className="h-10 appearance-none rounded-md border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm font-medium text-gray-700 outline-none focus:border-ardis-blue focus:ring-2 focus:ring-ardis-blue/20" data-id="element-312" data-mp="862fzjkrbljo">
                  <option value="default" data-id="element-313" data-mp="mxkrb42o8rwp">Tri par défaut</option>
                  <option value="name-asc" data-id="element-314" data-mp="qxd4xv6x9o8x">Nom, A à Z</option>
                  <option value="name-desc" data-id="element-315" data-mp="qvsae5kly5b2">Nom, Z à A</option>
                </select>
              </label>
            </div>
          </div>

          <motion.div layout className={view === 'grid' ? 'mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 xl:grid-cols-3' : 'mt-6 space-y-4'} data-id="element-316" data-mp="27dsmei6kqywb">
            {products.map((product, index) => {
            const quoteUrl = `mailto:info@lepiandistribution.com?subject=${encodeURIComponent(`Demande de devis — ${product.name}`)}`;
            return <motion.article layout key={product.name} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.28,
              delay: index * 0.04
            }} className={view === 'grid' ? 'flex flex-col' : 'flex flex-col gap-5 rounded-xl border border-gray-100 p-4 sm:flex-row sm:items-center'} data-id="element-317" data-mp="2v2lcvz4fgejj" data-mpi={index}>
                  <div className={view === 'grid' ? 'aspect-square overflow-hidden bg-white' : 'h-36 w-full flex-shrink-0 overflow-hidden bg-white sm:w-44'} data-id="element-318" data-mp="3dwh1ct3f4l3d"><img src={product.image} alt={product.name} className="h-full w-full object-contain" data-id="element-319" data-mp="2hfkxvfvar7jt" /></div>
                  <div className={view === 'grid' ? 'flex flex-1 flex-col pt-4' : 'flex flex-1 flex-col'} data-id="element-320" data-mp="2m7334g4crual">
                    <p className="text-xs font-medium text-ardis-green/70" data-id="element-321" data-mp="28gg3mlqrq0dg">{product.collection}</p>
                    <h5 className="mt-2 font-display text-sm font-semibold leading-relaxed text-gray-900" data-id="element-322" data-mp="2ci1i2biyampr">{product.name}</h5>
                    <a href={quoteUrl} className="mt-4 inline-flex w-fit items-center justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2" data-id="element-323" data-mp="307dq382jkoqh">Demandez un devis</a>
                  </div>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}